import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { PageShell, PageHero } from "@/components/site/page-shell";
import { supabase } from "@/integrations/supabase/client";
import { useCategoryTree } from "@/hooks/use-category-tree";

export const Route = createFileRoute("/blog/category/$slug")({
  component: BlogCategoryPage,
  head: ({ params }) => ({
    meta: [
      { title: `${params.slug} — Blog` },
      { name: "description", content: `Articles in the ${params.slug} category.` },
      { property: "og:title", content: `${params.slug} — Blog` },
    ],
  }),
});

function BlogCategoryPage() {
  const { slug } = Route.useParams();
  const { data: tree, isLoading: catLoading } = useCategoryTree("categories", slug);
  const current = tree?.current;
  const ids = current ? [current.id, ...tree!.descendants.map((d) => d.id)] : [];

  const { data: posts, isLoading } = useQuery({
    queryKey: ["public", "posts", "by-cats", ids],
    enabled: ids.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("posts")
        .select("id,title,slug,excerpt,cover_url,category_id,published_at,created_at")
        .eq("published", true)
        .in("category_id", ids)
        .order("published_at", { ascending: false, nullsFirst: false });
      if (error) throw error;
      return data ?? [];
    },
  });

  if (!catLoading && !current) throw notFound();

  return (
    <PageShell>
      <PageHero
        eyebrow={tree?.ancestors.map((a) => a.name).join(" › ") || "Blog"}
        title={current?.name ?? slug}
        sub={current?.description ?? "All articles in this category."}
      />
      <section className="py-12">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <nav className="mb-6 text-sm text-muted-foreground">
            <Link to="/blog" search={{ q: "", cat: "", page: 1, view: "grid" }} className="hover:text-foreground">Blog</Link>
            {tree?.ancestors.map((a) => (
              <span key={a.id}>
                {" › "}
                <Link to="/blog/category/$slug" params={{ slug: a.slug }} search={{ q: "", cat: "", page: 1, view: "grid" }} className="hover:text-foreground">{a.name}</Link>
              </span>
            ))}
            <span> › <span className="text-foreground">{current?.name}</span></span>
          </nav>

          {tree && tree.children.length > 0 && (
            <div className="mb-8 flex flex-wrap gap-2">
              {tree.children.map((c) => (
                <Link key={c.id} to="/blog/category/$slug" params={{ slug: c.slug }} search={{ q: "", cat: "", page: 1, view: "grid" }} className="rounded-full border border-border bg-card px-3 py-1.5 text-sm hover:border-primary/40">
                  {c.name}
                </Link>
              ))}
            </div>
          )}

          {(isLoading || catLoading) && <p className="text-center text-muted-foreground">Loading…</p>}
          {!isLoading && !posts?.length && <p className="text-center text-muted-foreground">No articles in this category yet.</p>}

          <div className="grid gap-5 md:grid-cols-3">
            {posts?.map((p: any, i: number) => {
              const date = p.published_at ?? p.created_at;
              return (
                <Link key={p.id} to="/blog/$slug" params={{ slug: p.slug }} search={{ q: "", cat: "", page: 1, view: "grid" }} className="group rounded-2xl border border-border bg-card overflow-hidden hover:border-primary/40 transition">
                  {p.cover_url ? (
                    <img src={p.cover_url} alt={p.title} className="aspect-[16/10] w-full object-cover" loading="lazy" />
                  ) : (
                    <div className={`aspect-[16/10] bg-gradient-to-br ${i % 3 === 0 ? "from-violet-500 to-fuchsia-500" : i % 3 === 1 ? "from-sky-500 to-cyan-500" : "from-amber-500 to-pink-500"}`} />
                  )}
                  <div className="p-5">
                    <div className="text-xs text-muted-foreground">{format(new Date(date), "MMM d, yyyy")}</div>
                    <h2 className="mt-2 text-lg font-semibold group-hover:text-primary transition">{p.title}</h2>
                    {p.excerpt && <p className="mt-2 text-sm text-muted-foreground line-clamp-3">{p.excerpt}</p>}
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </PageShell>
  );
}
